import React, { useMemo, useState, useCallback } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useWidgetTheme } from '@brandclub/bc-widgets';
import { ConciergeWidget } from './ConciergeWidget';
import { ConciergeFloatingButton } from './ConciergeFloatingButton';
import { ConciergeContextProvider } from './ConciergeContextProvider';
import type { ConciergeCommerceAdapter } from './types/commerce';
import { getConciergeSignInUrl } from '../Auth/shopifyLogin';
import { ShadowDomWrapper } from './ShadowDomWrapper';
import { ConciergeWidgetState, PageContext } from './types/widget';
import { generateConciergeCSSFromWidgetTheme } from './theme';

const queryClient = new QueryClient();

export interface ConciergeStandaloneProps {
  widgetState: ConciergeWidgetState;
  pageContext?: PageContext;
  showMaximize?: boolean;
  /** Whether the widget is in maximized (modal) mode */
  isMaximized?: boolean;
  onMaximize?: () => void;
  /** Called when user clicks minimize button in maximized mode */
  onMinimize?: () => void;
  title?: string;
  isConciergeEnabled?: boolean;
  /**
   * CONTROLLED mode (opt-in by the mounting caller). `ConciergeWidget` itself
   * re-checks `isConciergeEnabled` before rendering the pane (its own Split-off
   * self-gate — the same flag used for proactive UI everywhere else). That means
   * an explicit `open()` from a controlled caller (e.g. the Pages storefront,
   * whose chips already decided concierge is on for THIS page) would still
   * render nothing when the checkout app's own rollout is off. Setting this
   * bypasses that inner gate for the pane only — `ConciergeFloatingButton`
   * (proactive UI) keeps using `isConciergeEnabled` unchanged.
   */
  conciergeControlled?: boolean;
  hideOnConciergePage?: boolean;
  /** Base URL (origin only, e.g. 'https://shop.example.com') for API requests.
   * /api-rewards is prepended internally — do NOT include it here. */
  apiBaseUrl?: string;
  /** Initial query to auto-send when widget opens (passed via props to avoid cross-bundle Zustand issues) */
  initialQuery?: string;
  /** Stackline SKU associated with the initial query (for product context) */
  initialQueryStacklineSku?: string;
  /** Called after the initial query has been processed/sent */
  onInitialQueryProcessed?: () => void;
  /** Brand ID for fetching suggested queries. If not provided, will try to get from brand config */
  brandId?: number;
  /** Explicit override for the dedicated concierge page URL (expand/history icons).
   *  Hosts should pass the server-computed `canonicalConciergeUrl` from the brand-app
   *  config when they have it (the brand's chosen serving surface — Plan 6 / D-23),
   *  never a re-derived URL. If not provided, the chat widget resolves it from its own
   *  brand-app config fetch, falling back to window.location.origin + '/concierge'. */
  conciergePageUrl?: string;
  /** Optional app identity supplied by embed hosts that already loaded bootstrap/config. */
  appId?: string;
  appName?: string;
  /** Optional shared query client supplied by embed hosts to avoid duplicate config fetches. */
  queryClient?: QueryClient;
  /**
   * The bag that "Add to bag" fills. The HOST picks it, because only the host
   * knows whether checkout is on and whether its own cart is mounted. Omit it
   * when there is no bag: then every buy control stays hidden and the product
   * panel shows only "View".
   */
  commerce?: ConciergeCommerceAdapter;
}

/**
 * Inner component that uses hooks requiring QueryClientProvider
 */
const ConciergeStandaloneInner: React.FC<ConciergeStandaloneProps> = ({
  widgetState,
  pageContext,
  showMaximize = false,
  isMaximized = false,
  onMaximize,
  onMinimize,
  title,
  isConciergeEnabled = true,
  conciergeControlled = false,
  hideOnConciergePage = false,
  apiBaseUrl,
  initialQuery: externalInitialQuery,
  initialQueryStacklineSku,
  onInitialQueryProcessed,
  brandId,
  conciergePageUrl,
  appId,
  appName,
  commerce,
}) => {
  // Internal state for suggested pill queries (avoids Zustand cross-bundle issues)
  const [pillQuery, setPillQuery] = useState<string | null>(null);

  // Fetch theme from bc-widgets theme system (the only theming system)
  const { data: widgetThemeData } = useWidgetTheme();

  // Generate theme CSS directly from widget theme data
  const themeCSS = useMemo(() => {
    return generateConciergeCSSFromWidgetTheme(widgetThemeData);
  }, [widgetThemeData]);

  // Handle suggested pill click from floating button
  const handleSuggestedPillClick = useCallback((message: string) => {
    setPillQuery(message);
  }, []);

  // Handle initial query processed - clear internal pill query if it was the source
  const handleInitialQueryProcessed = useCallback(() => {
    setPillQuery(null);
    onInitialQueryProcessed?.();
  }, [onInitialQueryProcessed]);

  // Determine which initial query to use: pill query takes precedence if set
  const effectiveInitialQuery = pillQuery || externalInitialQuery;

  // The embed bubble has no sign-in machinery of its own: the inline phone/OTP
  // form needs Amplify plus the Redux profile reload, both of which live in the
  // landing-page bundle. With no `requestSignIn` here, `PromptLoginPart` had
  // nothing to render and a signed-out shopper who asked the bubble about an
  // order was told to "sign in here" with no "here" to click. Hand off to the
  // surface that CAN sign them in instead. Hosts that own a real inline flow
  // (landing-page) mount their own provider and are unaffected.
  const requestSignIn = useCallback(() => {
    const url = getConciergeSignInUrl();
    if (url) window.location.assign(url);
  }, []);

  // Every buy control in the concierge is gated on `commerce` being present
  // (the product panel's "Add to bag", and the widget loader for the product
  // cards). This component used to build the Shopify storefront cart here by
  // itself, so the bubble filled the Shopify cart even for a brand with checkout
  // on. The host now picks the bag and passes it in.

  return (
    <ConciergeContextProvider apiBaseUrl={apiBaseUrl} requestSignIn={requestSignIn} commerce={commerce}>
      {/* Floating button stays outside Shadow DOM for viewport positioning */}
      <ConciergeFloatingButton
        widgetState={widgetState}
        isConciergeEnabled={isConciergeEnabled}
        hideOnConciergePage={hideOnConciergePage}
        appId={appId}
        appName={appName}
        onSuggestedPillClick={handleSuggestedPillClick}
      />

      {/* Widget wrapped in Shadow DOM for style isolation */}
      {widgetState.isOpen && (
        <ShadowDomWrapper themeCSS={themeCSS}>
          <ConciergeWidget
            widgetState={widgetState}
            pageContext={pageContext}
            showMaximize={showMaximize}
            isMaximized={isMaximized}
            onMaximize={onMaximize}
            onMinimize={onMinimize}
            title={title}
            isConciergeEnabled={isConciergeEnabled || conciergeControlled}
            disablePortal={true}
            initialQuery={effectiveInitialQuery}
            initialQueryStacklineSku={pillQuery ? undefined : initialQueryStacklineSku}
            onInitialQueryProcessed={handleInitialQueryProcessed}
            brandId={brandId}
            conciergePageUrl={conciergePageUrl}
          />
        </ShadowDomWrapper>
      )}
    </ConciergeContextProvider>
  );
};

/**
 * Self-contained Concierge component that bundles all required providers.
 * Uses Shadow DOM for complete style isolation, ensuring the widget looks
 * identical regardless of the host application's CSS.
 *
 * Theme is automatically loaded from the bc-widgets theme system.
 * Configure themes using the widget theme editor - this is the only theming system.
 *
 * Use this for apps that don't have full provider setup (like checkout-shop-app).
 *
 * @example
 * // Basic usage - theme loaded automatically from bc-widgets theme system
 * <ConciergeStandalone widgetState={widgetState} />
 */
export const ConciergeStandalone: React.FC<ConciergeStandaloneProps> = (props) => {
  return (
    <QueryClientProvider client={props.queryClient ?? queryClient}>
      <ConciergeStandaloneInner {...props} />
    </QueryClientProvider>
  );
};
